import * as os from 'node:os';
import * as winston from 'winston';
import * as config from './config';
import * as nodeUtils from 'node:util';
import * as utils from '../utils';
import chalk from 'chalk';
import dayjs from 'dayjs';
import { getRunnerVersion } from '../utils';

const hostname = os.hostname();
const runnerVersion = getRunnerVersion();

type LogTransport = winston.transport & { waitForFlush(): Promise<void>; setProxy(proxy: string): void; name: string };

// Module-level state for remote transports (needed for FF-based adjustment)
let remoteTransports: LogTransport[] = [];
let betterStackTransportAdded = false;

function getStreamsAndWaitForFlushPromise() {
    const transports: winston.transport[] = [];

    // Coralogix added immediately; BetterStack deferred until setBetterStackCredentials() (region-specific)
    if (!config.IS_ON_PREM) {
        // TODO: Coralogix cleanup - Remove this block once migration to BetterStack is complete
        if (config.CORALOGIX_PRIVATE_KEY) {
            const { CoralogixTransport } = require('../lib/coralogix-winston.transport') as typeof import('../lib/coralogix-winston.transport');
            remoteTransports.push(new CoralogixTransport({ category: 'ROOT' }));
        }
        transports.push(...remoteTransports);
    }

    const waitForFlush = async () => { await Promise.all(remoteTransports.map(t => t.waitForFlush())); };
    const setProxy = (proxy: string) => remoteTransports.forEach(t => t.setProxy(proxy));

    if (config.LOGGER_CONSOLE) {
        const inspectOptions: nodeUtils.InspectOptions = { depth: 5, colors: true };
        transports.push(new winston.transports.Console({
            format: winston.format.combine(
                winston.format.colorize({ all: true, colors: { info: 'blue', error: 'red', warning: 'yellow' } }),
                winston.format.timestamp({ format: 'HH:mm:ss:SSS' }),
                winston.format.printf(info => `[${info.timestamp}] ${info.level}\t ${info.category}: ${info.message} ${nodeUtils.inspect(info.meta, inspectOptions)}`),
            ),
        }));
    }

    return [transports, waitForFlush, setProxy] as const;
}

const inspectOptions: nodeUtils.InspectOptions = { depth: 5, colors: true };
export const sessionPlayerLoggerFormatter: typeof import('clickim/src/lib/logger').Logger['formatter'] = (level, message, data) => {
    const date = `[${dayjs().format('HH:mm:ss:SSS')}]`;
    const formattedMessage = nodeUtils.inspect(message, inspectOptions);
    const formattedData = nodeUtils.inspect(data, inspectOptions);
    return `${chalk.white(date)} ${chalk.reset()}SessionPlayer-${chalk.red(level)}\t ${formattedMessage} ${formattedData}`;
};

const [transports, waitForFlush, setProxy] = getStreamsAndWaitForFlushPromise();
const level = config.LOGGER_DEBUG ? 'debug' : 'info';
const defaultMeta = {
    release: Boolean(global.meta?.IS_RELEASE),
    branch: (global.meta?.IS_RELEASE && 'production') || (!global.meta?.IS_LOCAL && global.meta?.BRANCH_NAME) || 'local',
};

const winstonMainLogger = winston.createLogger({
    levels: winston.config.syslog.levels,
    level,
    transports,
    defaultMeta: { name: 'runner', hostname, nodeVersion: process.version, runnerVersion, ...defaultMeta },
});

// Deferred - BetterStack transport is added when credentials are set (region-specific)
let betterStackCredentials: { ingestUrl: string; sourceToken: string } | undefined;

export function getBetterStackCredentials() {
    return betterStackCredentials;
}

export function setBetterStackCredentials(ingestUrl: string, token: string) {
    if (betterStackTransportAdded || config.IS_ON_PREM || !ingestUrl || !token) {
        return;
    }
    // Make sure the ingest url starts with https://
    const normalizedIngestUrl = ingestUrl.startsWith('https://') ? ingestUrl : `https://${ingestUrl}`;
    betterStackCredentials = { ingestUrl: normalizedIngestUrl, sourceToken: token };
    const { BetterStackWinstonTransport } = require('../lib/betterstack-winston.transport') as typeof import('../lib/betterstack-winston.transport');
    const transport = new BetterStackWinstonTransport({ ingestUrl, token });
    remoteTransports.push(transport);
    winstonMainLogger.add(transport);
    betterStackTransportAdded = true;

    // Adjust logger transports based on feature flags.
    // We also want to adjust the logger transports when the betterstack logger is set,
    adjustLoggersFromFeatureFlags();

}

// Adjusts logger transports based on feature flags.
export function adjustLoggersFromFeatureFlags() {
    // Dynamic require to avoid circular dependency (featureFlagsService imports logger)
    const { featureFlagsService } = require('./featureFlagsService') as typeof import('./featureFlagsService');

    // Copy array since we modify remoteTransports inside the loop
    for (const transport of [...remoteTransports]) {
        const shouldDisable =
            (transport.name === 'Coralogix Transport' && featureFlagsService.flags.disableCoralogixLogger.isEnabled()) ||
            (transport.name === 'BetterStack Transport' && featureFlagsService.flags.disableBetterStackLogger.isEnabled());
        if (shouldDisable) {
            winstonMainLogger.remove(transport);
            remoteTransports = remoteTransports.filter(t => t !== transport);
        }
    }
}

let executionId: string | null | undefined = null;
let projectId: string | null | undefined = null;

export function setExecutionId(execId?: string | null) {
    executionId = execId;
}

export function setProjectId(projId?: string | null) {
    projectId = projId;
}

export function setProxyUri(proxyUri: string | undefined) {
    if (config.IS_ON_PREM || !proxyUri) return;
    setProxy(proxyUri);
}

function addExecutionMetadata(dataExecutionId?: string) {
    return {
        projectId,
        time: new Date().toISOString(),
        region: Logger.getRegion(),
        ...(executionId && !dataExecutionId && { executionId }),
    };
}

type Level = 'debug' | 'info' | 'warning' | 'error' | 'crit';

export class Logger {
    private static region: string | undefined;

    // We set the region in initializeUserWithAuth, so we can use it here for the logs
    public static setRegion(region: string) {
        Logger.region = region;
    }

    public static getRegion() {
        return Logger.region;
    }

    constructor(private readonly _logger: winston.Logger) {
        this.debug = this.debug.bind(this);
        this.info = this.info.bind(this);
        this.warn = this.warn.bind(this);
        this.error = this.error.bind(this);
        this.fatal = this.fatal.bind(this);
    }

    debug(msg: string, data: object = {}) {
        this.innerLog('debug', msg, data);
    }

    info(msg: string, data: object = {}) {
        this.innerLog('info', msg, data);
    }

    warn(msg: string, data: object = {}) {
        this.innerLog('warning', msg, data);
    }

    error(msg: string, data: object = {}) {
        this.innerLog('error', msg, data);
    }

    fatal(msg: string, data: object = {}) {
        this.innerLog('crit', msg, data);
    }

    private innerLog(logLevel: Level, msg: string, data: object = {}) {
        const { executionId: dataExecutionId } = data as { [key: string]: any };
        if ((data as any).user) {
            (data as any).user = utils.maskObjectFields((data as any).user, ['email']);
        }
        try {
            this._logger.log(logLevel, { meta: data, message: msg, ...addExecutionMetadata(dataExecutionId) });
        } catch (err) {
            try {
                this._logger.log('crit', { message: `failed to log message ${err.message}, ${err.stack}`, ...addExecutionMetadata(dataExecutionId) });
            } catch { /* well what can we do */ }
        }
    }

    waitForFlush() {
        return waitForFlush();
    }
}

export function getLogger(loggerName: string) {
    return new Logger(winstonMainLogger.child({ category: loggerName }));
}
